import React, { useEffect, useState, useRef } from "react";
import { Bell, Check, X } from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";

export default function NotificationBell() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const h = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(h);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read"); load();
  };
  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`); load();
  };

  const iconFor = (type) => {
    if (type === "order_assigned") return "📦";
    if (type === "order_placed") return "🛒";
    if (type === "order_overdue") return "⚡";
    if (type === "upi_verified") return "✅";
    return "🔔";
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="notif-bell"
        className="relative p-2 rounded-xl hover:bg-white/60 transition"
      >
        <Bell className="w-5 h-5 text-[#1D1D1F]" />
        {unread > 0 && (
          <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#FF3B30] text-white text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl soft-shadow-md border border-gray-100 z-50 overflow-hidden fade-up">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="heading-font font-bold">{t("notifications")}</div>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="notif-mark-all" className="text-xs text-[#0055FF] font-bold hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> {t("mark_all_read")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-[#86868B] text-sm">{t("no_notifications")}</div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left p-4 border-b border-gray-50 hover:bg-blue-50/30 flex items-start gap-3 transition ${!n.read ? "bg-blue-50/20" : ""}`}
              >
                <div className="text-xl shrink-0">{iconFor(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${!n.read ? "font-bold" : "font-semibold"} text-[#1D1D1F] truncate`}>{n.title}</div>
                  {n.body && <div className="text-xs text-[#86868B] truncate">{n.body}</div>}
                  <div className="text-[10px] text-[#A1A1A6] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && <span className="w-2 h-2 bg-[#0055FF] rounded-full shrink-0 mt-1.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
